import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { priceBreakdownsService } from '../services/api';
import { BarChart3 as BarChart3Icon, Trash2 as Trash2Icon } from 'lucide-react';

const BREAKDOWN_TYPES = [
  { value: 'TAX', label: 'Tax' },
  { value: 'DISCOUNT', label: 'Discount' },
  { value: 'FEE', label: 'Fee' },
  { value: 'SURCHARGE', label: 'Surcharge' },
  { value: 'DEPOSIT', label: 'Deposit' },
  { value: 'REFUND', label: 'Refund' },
  { value: 'ADJUSTMENT', label: 'Adjustment' },
  { value: 'OTHER', label: 'Other' }
];

const CALCULATION_TYPES = [
  { value: 'PERCENTAGE', label: 'Percentage (%)', icon: '%' },
  { value: 'FIXED', label: 'Fixed Amount', icon: '₹' },
  { value: 'TIERED', label: 'Tiered' }
];

/**
 * PriceBreakdownModal - Reusable component for viewing and creating price breakdowns
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal open state
 * @param {Function} props.onClose - Close handler
 * @param {string} props.entityType - Type of entity ('roomType', 'course', 'addOnService')
 * @param {string} props.entityId - ID of the entity
 * @param {Object} props.entity - The entity object (with name, basePrice, etc.)
 * @param {Function} props.onUpdate - Callback after breakdowns are updated
 */
function PriceBreakdownModal({ isOpen, onClose, entityType, entityId, entity, onUpdate }) {
  const [breakdowns, setBreakdowns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    breakdownType: 'TAX',
    calculationType: 'PERCENTAGE',
    value: '',
    isActive: true
  });

  useEffect(() => {
    if (isOpen && entityId) {
      loadBreakdowns();
    }
  }, [isOpen, entityId]);

  const loadBreakdowns = async () => {
    setLoading(true);
    try {
      let response;
      
      if (entityType === 'roomType') {
        response = await priceBreakdownsService.getRoomTypeBreakdowns(entityId, true);
      } else if (entityType === 'course') {
        response = await priceBreakdownsService.getCourseBreakdowns(entityId, true);
      } else if (entityType === 'addOnService') {
        response = await priceBreakdownsService.getAddOnServiceBreakdowns(entityId, true);
      }

      setBreakdowns(response?.data?.priceBreakdowns || []);
    } catch (error) {
      console.error('Error loading price breakdowns:', error);
      toast.error('Failed to load price breakdowns');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const calculateAmount = () => {
    const totalPrice = getTotalPrice();
    const value = parseFloat(formData.value || 0);
    
    if (formData.calculationType === 'PERCENTAGE') {
      return (totalPrice * value / 100).toFixed(2);
    } else {
      return value.toFixed(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.value) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      const calculatedAmount = parseFloat(calculateAmount());
      
      const breakdownData = {
        name: formData.name,
        amount: calculatedAmount,
        breakdownType: formData.breakdownType,
        calculationType: formData.calculationType,
        value: parseFloat(formData.value),
        isActive: formData.isActive
      };

      // Add entity-specific ID
      if (entityType === 'roomType') {
        breakdownData.roomTypeId = entityId;
        await priceBreakdownsService.createRoomTypeBreakdown(breakdownData);
      } else if (entityType === 'course') {
        breakdownData.courseId = entityId;
        await priceBreakdownsService.createCourseBreakdown(breakdownData);
      } else if (entityType === 'addOnService') {
        breakdownData.addOnServiceId = entityId;
        await priceBreakdownsService.createAddOnServiceBreakdown(breakdownData);
      }

      toast.success('Price breakdown created successfully');
      
      // Reset form
      setFormData({
        name: '',
        amount: '',
        breakdownType: 'TAX',
        calculationType: 'PERCENTAGE',
        value: '',
        isActive: true
      });
      
      setShowCreateForm(false);
      loadBreakdowns();
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error creating price breakdown:', error);
      toast.error(error?.error || 'Failed to create price breakdown');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await priceBreakdownsService.update(id, { isActive: !currentStatus });
      toast.success(`Breakdown ${!currentStatus ? 'activated' : 'deactivated'}`);
      loadBreakdowns();
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating breakdown:', error);
      toast.error('Failed to update breakdown');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this price breakdown?')) {
      return;
    }

    try {
      await priceBreakdownsService.delete(id);
      toast.success('Price breakdown deleted successfully');
      loadBreakdowns();
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error deleting breakdown:', error);
      toast.error('Failed to delete breakdown');
    }
  };

  const getEntityLabel = () => {
    if (entityType === 'roomType') return 'Room Type';
    if (entityType === 'course') return 'Course';
    if (entityType === 'addOnService') return 'Add-On Service';
    return 'Entity';
  };

  const getTotalPrice = () => {
    // Note: basePrice is actually the TOTAL PRICE (confusing naming in backend)
    // The breakdowns are PART OF this total, not added to it
    return parseFloat(entity?.basePrice || entity?.fee || entity?.price || 0);
  };

  const calculateTotalBreakdowns = () => {
    return breakdowns
      .filter(b => b.isActive)
      .reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0);
  };

  const calculateRemainingAmount = () => {
    // Total Price - Sum of Breakdowns = Remaining/Base Amount
    const totalPrice = getTotalPrice();
    const breakdownsTotal = calculateTotalBreakdowns();
    return totalPrice - breakdownsTotal;
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#E9DFCE',
        borderRadius: '12px',
        maxWidth: '800px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #E3DFD3',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 1
        }}>
          <div>
            <h2 style={{ margin: '0 0 8px 0', color: '#1A3E2A', fontSize: '24px' }}>
              Price Breakdowns
            </h2>
            <p style={{ margin: 0, color: '#5F6B61', fontSize: '14px' }}>
              {getEntityLabel()}: <strong>{entity?.name}</strong>
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#5F6B61',
              padding: '4px 8px'
            }}
          >
            ✕
          </button>
        </div>

        {/* Summary Card */}
        <div style={{
          margin: '24px',
          padding: '20px',
          backgroundColor: '#F0F3EE',
          border: '2px solid #9DB7A5',
          borderRadius: '8px'
        }}>
          {/* Total Price - Main Display */}
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '20px',
            paddingBottom: '20px',
            borderBottom: '2px solid #9DB7A5'
          }}>
            <div style={{ fontSize: '14px', color: '#5F6B61', marginBottom: '8px', fontWeight: '500' }}>
              Total Price
            </div>
            <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#1A3E2A' }}>
              ₹{getTotalPrice().toFixed(2)}
            </div>
          </div>

          {/* Breakdown Composition */}
          <div style={{ 
            backgroundColor: '#E1E9E2',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '12px'
          }}>
            <div style={{ fontSize: '12px', color: '#163626', fontWeight: '600', marginBottom: '12px', textTransform: 'uppercase' }}>
              Price Breakdown Composition
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '14px', color: '#36433B' }}>Total Breakdowns:</span>
              <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#1A3E2A' }}>
                ₹{calculateTotalBreakdowns().toFixed(2)}
              </span>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', color: '#36433B' }}>Remaining Amount:</span>
              <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#5F6B61' }}>
                ₹{calculateRemainingAmount().toFixed(2)}
              </span>
            </div>

            {/* Visual Representation */}
            <div style={{
              height: '8px',
              backgroundColor: '#E3DFD3',
              borderRadius: '4px',
              overflow: 'hidden',
              marginTop: '12px'
            }}>
              <div style={{
                height: '100%',
                width: `${(calculateTotalBreakdowns() / getTotalPrice() * 100).toFixed(1)}%`,
                backgroundColor: '#1A3E2A',
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              marginTop: '4px',
              fontSize: '11px',
              color: '#5F6B61'
            }}>
              <span>Breakdowns: {(calculateTotalBreakdowns() / getTotalPrice() * 100).toFixed(1)}%</span>
              <span>Remaining: {(calculateRemainingAmount() / getTotalPrice() * 100).toFixed(1)}%</span>
            </div>
          </div>

          {/* Formula Display */}
          <div style={{
            textAlign: 'center',
            fontSize: '13px',
            color: '#5F6B61',
            fontFamily: 'monospace',
            padding: '12px',
            backgroundColor: '#F5F3EB',
            borderRadius: '6px'
          }}>
            ₹{calculateTotalBreakdowns().toFixed(2)} (breakdowns) + ₹{calculateRemainingAmount().toFixed(2)} (base) = ₹{getTotalPrice().toFixed(2)}
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '0 24px 24px 24px' }}>
          {/* Create Button */}
          {!showCreateForm && (
            <button
              onClick={() => setShowCreateForm(true)}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#1A3E2A',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '20px'
              }}
            >
              Add New Breakdown
            </button>
          )}

          {/* Create Form */}
          {showCreateForm && (
            <div style={{
              border: '2px solid #1A3E2A',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px',
              backgroundColor: '#F0F3EE'
            }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#0F2619' }}>Create New Breakdown</h3>
              
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gap: '12px' }}>
                  {/* Name */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '14px' }}>
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., GST (18%)"
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  {/* Breakdown Type */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '14px' }}>
                      Breakdown Type *
                    </label>
                    <select
                      name="breakdownType"
                      value={formData.breakdownType}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    >
                      {BREAKDOWN_TYPES.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Calculation Type */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '14px' }}>
                      Calculation Type *
                    </label>
                    <select
                      name="calculationType"
                      value={formData.calculationType}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    >
                      {CALCULATION_TYPES.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Value */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '14px' }}>
                      Value * {formData.calculationType === 'PERCENTAGE' ? '(%)' : '(₹)'}
                    </label>
                    <input
                      type="number"
                      name="value"
                      value={formData.value}
                      onChange={handleInputChange}
                      placeholder={formData.calculationType === 'PERCENTAGE' ? '18' : '500'}
                      step="0.01"
                      min="0"
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                    {formData.value && (
                      <div style={{ marginTop: '4px', fontSize: '12px', color: '#5F6B61' }}>
                        Calculated Amount: ₹{calculateAmount()}
                      </div>
                    )}
                  </div>

                  {/* Active Status */}
                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      <span style={{ fontSize: '14px' }}>Active</span>
                    </label>
                  </div>
                </div>

                {/* Form Actions */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                  <button
                    type="submit"
                    disabled={creating}
                    style={{
                      flex: 1,
                      padding: '10px',
                      backgroundColor: creating ? '#A5A89B' : '#1A3E2A',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: creating ? 'not-allowed' : 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    {creating ? 'Creating...' : 'Create Breakdown'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setFormData({
                        name: '',
                        amount: '',
                        breakdownType: 'TAX',
                        calculationType: 'PERCENTAGE',
                        value: '',
                        isActive: true
                      });
                    }}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#5F6B61',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Breakdowns List */}
          <div>
            <h3 style={{ margin: '0 0 16px 0', color: '#1A3E2A' }}>
              Existing Breakdowns ({breakdowns.length})
            </h3>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#5F6B61' }}>
                Loading breakdowns...
              </div>
            ) : breakdowns.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                backgroundColor: '#F5F3EB',
                borderRadius: '8px',
                color: '#5F6B61'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}><BarChart3Icon size="1em" aria-hidden="true" /></div>
                <p style={{ margin: 0 }}>No price breakdowns created yet</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '12px' }}>
                {breakdowns.map(breakdown => {
                  const typeInfo = BREAKDOWN_TYPES.find(t => t.value === breakdown.breakdownType);
                  const calcInfo = CALCULATION_TYPES.find(c => c.value === breakdown.calculationType);
                  
                  return (
                    <div
                      key={breakdown.id}
                      style={{
                        border: `2px solid ${breakdown.isActive ? '#E3DFD3' : '#D48A7C'}`,
                        borderRadius: '8px',
                        padding: '16px',
                        backgroundColor: breakdown.isActive ? 'white' : '#F9EEEB',
                        opacity: breakdown.isActive ? 1 : 0.7
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <h4 style={{ margin: 0, color: '#1A3E2A', fontSize: '16px' }}>
                              {breakdown.name}
                            </h4>
                            <span style={{
                              fontSize: '11px',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              backgroundColor: breakdown.isActive ? '#E1E9E2' : '#F2D9D3',
                              color: breakdown.isActive ? '#163626' : '#8E3628',
                              fontWeight: '500'
                            }}>
                              {breakdown.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          
                          <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#5F6B61' }}>
                            <div>
                              <strong>Type:</strong> {typeInfo?.label}
                            </div>
                            <div>
                              <strong>Calculation:</strong> {calcInfo?.label}
                            </div>
                            <div>
                              <strong>Value:</strong> {breakdown.calculationType === 'PERCENTAGE' ? `${breakdown.value}%` : `₹${breakdown.value}`}
                            </div>
                          </div>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1A3E2A' }}>
                              ₹{parseFloat(breakdown.amount).toFixed(2)}
                            </div>
                            <div style={{ fontSize: '11px', color: '#5F6B61' }}>Amount</div>
                          </div>
                          
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button
                              onClick={() => handleToggleActive(breakdown.id, breakdown.isActive)}
                              title={breakdown.isActive ? 'Deactivate' : 'Activate'}
                              style={{
                                padding: '6px 10px',
                                backgroundColor: breakdown.isActive ? '#C9903C' : '#1A3E2A',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              {breakdown.isActive ? '' : ''}
                            </button>
                            <button
                              onClick={() => handleDelete(breakdown.id)}
                              title="Delete"
                              style={{
                                padding: '6px 10px',
                                backgroundColor: '#A13F2F',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              <Trash2Icon size="1em" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PriceBreakdownModal;
